import { useRef, useState } from 'react'
import { TextField, DropDown, Form, Label, Radio, Error, Modal } from '~components'
import { FuelButton, FuelToast } from '@frete.com/fuel-react'
import * as Styled from './styled'
import { useRequest } from 'ahooks'
import { Vehicle } from '~services'
import { sortData } from './helpers'
import { CategorizedOption, Option, FuelTextFieldEventProps, Values } from './types'
import Skeleton from './Skeleton'
import { object, string } from '~utils/validation'
import { useOnClickOutside, useStore } from '~hooks'

function VehicleData() {
  const store = useStore()
  const [unitOfMeasurement, setUnitOfMeasurement] = useState('tonelada')
  const [showError, setShowError] = useState(false)
  const [initialValues, setInitialValues] = useState<Values>({
    vehiclePlate: '',
    vehicleRntrc: '',
    vehicleType: {},
    vehicleBodyType: {},
    vehicleCapacity: '',
    vehicleTracker: {},
    vehicleLocator: '',
  })
  const modalRef = useRef<HTMLDivElement>()
  const { data: bodies, loading: loadingBodies } = useRequest(Vehicle.listBodies)
  const { data: locators, loading: loadingLocators } = useRequest(Vehicle.listLocators)
  const { data: vehicleResponse, runAsync } = useRequest(Vehicle.get, { onSuccess: handleVehicleSuccess, manual: true })
  const { data: trackers, loading: loadingTrackers } = useRequest(Vehicle.listTrackers, { onSuccess: runAsync })
  const { data: types, loading: loadingTypes } = useRequest(Vehicle.listTypes)
  const { runAsync: update, loading: loadingUpdate } = useRequest(Vehicle.update, { manual: true })
  const successToastReference = useRef<HTMLFuelToastElement>()
  const errorToastReference = useRef<HTMLFuelToastElement>()
  const typesOrder = ['pesado', 'medio', 'leve']
  const bodiesOrder = ['fechada', 'aberta', 'especial']
  const vehicleTypeOptions: CategorizedOption[] = []
  const vehicleBodyOptions: CategorizedOption[] = []
  const vehicleTrackerOptions: Option[] = trackers?.data?.map?.((tracker) => ({
    value: String(tracker.id),
    label: tracker.name,
  }))

  useOnClickOutside(modalRef, () => store.update({ saveVehicleDataModal: false }))

  const convertVehicleTypeTitle = {
    medio: 'Médios',
    leve: 'Leves',
    pesado: 'Pesados',
  }

  const convertVehicleBodyTitle = {
    fechada: 'Fechadas',
    aberta: 'Abertas',
    especial: 'Especiais',
  }

  if (bodies) {
    const sortedVehicleBodies = sortData(bodies?.data, 'type', bodiesOrder)
    sortedVehicleBodies.forEach((vehicleBody) => {
      const vehicleBodyType = vehicleBody.type as 'fechada' | 'aberta' | 'especial'
      const title = convertVehicleBodyTitle[vehicleBodyType]
      const vehicleBodyOptionIndex = vehicleBodyOptions.findIndex((option) => option.title === title)

      if (vehicleBodyOptionIndex !== -1)
        vehicleBodyOptions[vehicleBodyOptionIndex].options.push({
          label: vehicleBody.name,
          value: String(vehicleBody.id),
        })
      else vehicleBodyOptions.push({ title, options: [{ label: vehicleBody.name, value: String(vehicleBody.id) }] })
    })
  }

  if (types) {
    const sortedVehicleTypes = sortData(types?.data, 'category', typesOrder)

    sortedVehicleTypes.forEach((vehicleType) => {
      const vehicleTypeCategory = vehicleType.category as 'medio' | 'leve' | 'pesado'
      const title = convertVehicleTypeTitle[vehicleTypeCategory]
      const vehicleTypeOptionIndex = vehicleTypeOptions.findIndex((option) => option.title === title)

      if (vehicleTypeOptionIndex !== -1)
        vehicleTypeOptions[vehicleTypeOptionIndex].options.push({
          label: vehicleType.name,
          value: String(vehicleType.id),
        })
      else vehicleTypeOptions.push({ title, options: [{ label: vehicleType.name, value: String(vehicleType.id) }] })
    })
  }

  const validationSchema = object().shape({
    vehiclePlate: string().plate('Placa inválida.').required(),
    vehicleRntrc: string().rntrc('RNTRC inválido.').required(),
    vehicleType: object().shape({
      label: string(),
      value: string().required(),
    }),
    vehicleBodyType: object().shape({
      label: string(),
      value: string().required(),
    }),
    vehicleCapacity: string().capacity('Campo obrigatório.').required(),
    vehicleTracker: object().shape({
      label: string(),
      value: string().required(),
    }),
    vehicleLocator: string().required(),
  })

  async function handleSubmit(values: Values) {
    const [vehicle] = vehicleResponse.data
    const selectedType = types.data.find((type) => type.id === Number(values.vehicleType.value))
    const compositionId = selectedType.compositions[0].id

    try {
      await update(vehicle.id, {
        vehicle_type_id: Number(values.vehicleType.value),
        vehicle_body_id: Number(values.vehicleBodyType.value),
        vehicle_tracker_id: Number(values.vehicleTracker.value),
        vehicle_locator_id: Number(values.vehicleLocator),
        is_main: true,
        capacity: Number(values.vehicleCapacity.replace(',', '.')),
        plates: [
          {
            plate: values.vehiclePlate,
            rntrc: values.vehicleRntrc,
            vehicle_type_composition_id: compositionId,
          },
        ],
      })

      setInitialValues(values)
      await errorToastReference.current.close()
      await successToastReference.current.open()
      setTimeout(() => successToastReference.current.close(), 5000)
    } catch (error) {
      await successToastReference.current.close()
      await errorToastReference.current.open()

      setTimeout(() => errorToastReference.current.close(), 5000)
    }
  }

  function handleVehicleSuccess() {
    const vehicleCapacity =
      vehicleResponse?.data?.[0]?.vehicle_type?.capacity_measurement_unit === 'tonelada'
        ? Number(vehicleResponse?.data?.[0]?.capacity).toLocaleString('pt-BR', {
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
          })
        : String(vehicleResponse?.data?.[0]?.capacity)
    const vehicleTrackerOption = vehicleTrackerOptions.find(
      (vehicleTrackerOption) => vehicleTrackerOption.value == vehicleResponse?.data?.[0]?.vehicle_tracker?.id,
    )
    setInitialValues({
      vehiclePlate: vehicleResponse?.data?.[0]?.plates?.[0]?.plate || '',
      vehicleRntrc: vehicleResponse?.data?.[0]?.plates?.[0]?.rntrc || '',
      vehicleType: vehicleResponse?.data?.[0]?.vehicle_type?.id
        ? { label: vehicleResponse?.data?.[0]?.vehicle_type?.name, value: vehicleResponse?.data?.[0]?.vehicle_type?.id }
        : {},
      vehicleBodyType: vehicleResponse?.data?.[0]?.vehicle_body?.id
        ? { label: vehicleResponse?.data?.[0]?.vehicle_body?.name, value: vehicleResponse?.data?.[0]?.vehicle_body?.id }
        : {},
      vehicleCapacity: vehicleResponse?.data?.[0]?.capacity ? vehicleCapacity : '',
      vehicleTracker: vehicleTrackerOption
        ? { label: vehicleTrackerOption?.label, value: vehicleResponse?.data?.[0]?.vehicle_tracker?.id }
        : {},
      vehicleLocator: String(vehicleResponse?.data?.[0]?.vehicle_locator?.id) || '',
    })

    setUnitOfMeasurement(vehicleResponse?.data?.[0]?.vehicle_type?.capacity_measurement_unit)
  }

  function handleCloseModal() {
    store.update({ saveVehicleDataModal: false })
  }

  function handleCancelModa() {
    store.saveVehicleDataCallback()
    store.update({ saveVehicleDataModal: false })
  }

  const testRef = useRef<HTMLFuelTextFieldElement>()
  return (
    <Form<Values>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ errors, dirty, setFieldValue, submitForm }) => {
        if (store.saveVehicleDataModal && !dirty) {
          store.saveVehicleDataCallback()
          store.update({ saveVehicleDataModal: false })
        }

        const locatorError = showError && errors.vehicleLocator

        function handlePlateChange(event: CustomEvent<FuelTextFieldEventProps>) {
          setShowError(false)
          const valueWithoutCharacters = event.detail.value.replace(/[^a-z0-9]+/gi, '')
          const valueFormatted = valueWithoutCharacters.replace(/(.{3})(.+)/, '$1-$2')
          void setFieldValue('vehiclePlate', valueFormatted.toUpperCase())
        }

        function handleRntrcChange(event: CustomEvent<FuelTextFieldEventProps>) {
          setShowError(false)
          const valueWithOnlyNumbers = event.detail.value.replace(/[^0-9]+/gi, '')
          void setFieldValue('vehicleRntrc', valueWithOnlyNumbers)
        }

        function handleCapacityChange(event: CustomEvent<FuelTextFieldEventProps>) {
          setShowError(false)
          const valueWithOnlyNumbers = event.detail.value.replace(/[^0-9]+/gi, '')

          if (unitOfMeasurement === 'tonelada' && valueWithOnlyNumbers.length > 1) {
            void setFieldValue(
              'vehicleCapacity',
              (Number(valueWithOnlyNumbers) / 10).toLocaleString('pt-BR', {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }),
            )
          } else void setFieldValue('vehicleCapacity', valueWithOnlyNumbers)
        }

        function handleChangeVehicleType(newOption: Option, oldOption: Option) {
          setShowError(false)

          const fullNewOption = types.data.find((option) => String(option.id) === newOption.value)
          const fullOldOption = types.data.find((option) => String(option.id) === oldOption.value)

          setUnitOfMeasurement(fullNewOption.capacity_measurement_unit)

          if (fullNewOption?.capacity_measurement_unit !== fullOldOption?.capacity_measurement_unit) {
            void setFieldValue('vehicleCapacity', '')
          }

          if (fullOldOption?.capacity_measurement_unit) {
            void setFieldValue('vehiclePlate', '')
            void setFieldValue('vehicleRntrc', '')
          }
        }

        async function handleConfirmModal() {
          await submitForm()
          focusErrorElement()
          store.update({ saveVehicleDataModal: false })
        }

        function focusErrorElement() {
          setShowError(true)

          const errorKeys = Object.keys(errors)
          if (errorKeys.length > 0) {
            document.getElementsByName(errorKeys[0])[0].shadowRoot.querySelector('input').focus()
            document.getElementsByName(errorKeys[0])[0].shadowRoot.querySelector('input').scrollIntoView({
              behavior: 'smooth',
            })
          }
        }

        function setErrorVisible() {
          setShowError(false)
        }

        return (
          <Styled.Container $isButtonVisible={dirty}>
            <Styled.Title>Dados do veículo</Styled.Title>
            <Styled.Line />
            <Styled.Callout message="Todos os campos são obrigatórios." />
            {loadingBodies || loadingTrackers || loadingTypes || loadingLocators ? (
              <Skeleton />
            ) : (
              <>
                <TextField
                  showError={showError}
                  innerRef={testRef}
                  name="vehiclePlate"
                  label="Placa"
                  hint="Placa do veículo ou do cavalo."
                  maxLength={8}
                  onChange={handlePlateChange}
                  placeholder="Digite a placa"
                />
                <TextField
                  showError={showError}
                  name="vehicleRntrc"
                  label="RNTRC (ANTT)"
                  maxLength={9}
                  onChange={handleRntrcChange}
                  placeholder="Digite o número da ANTT"
                  type="tel"
                />
                <DropDown
                  showError={showError}
                  onChange={handleChangeVehicleType}
                  name="vehicleType"
                  label="Tipo de veículo"
                  options={vehicleTypeOptions}
                  placeholder="Selecione o tipo de veículo"
                />
                <DropDown
                  showError={showError}
                  name="vehicleBodyType"
                  label="Tipo de carroceria"
                  options={vehicleBodyOptions}
                  placeholder="Selecione o tipo de carroceria"
                  onChange={setErrorVisible}
                />
                <TextField
                  type="tel"
                  showError={showError}
                  name="vehicleCapacity"
                  label={`Capacidade máxima (${unitOfMeasurement})`}
                  maxLength={4}
                  placeholder="Digite a capacidade"
                  onChange={handleCapacityChange}
                />
                <DropDown
                  showError={showError}
                  name="vehicleTracker"
                  label="Rastreador"
                  options={vehicleTrackerOptions}
                  placeholder="Informe se o veículo possui rastreador"
                  onChange={setErrorVisible}
                />
                <Label color={locatorError ? 'danger-default' : 'neutral-strong'}>Localizador</Label>
                <div>
                  {locators.data.map((locator) => (
                    <Radio
                      name="vehicleLocator"
                      label={locator.name}
                      value={String(locator.id)}
                      onChange={setErrorVisible}
                    />
                  ))}
                </div>
                {locatorError && <Error>{locatorError}</Error>}
                {dirty && (
                  <Styled.Footer>
                    <Styled.Button
                      size="large"
                      type="submit"
                      fullWidth
                      isLoading={loadingUpdate}
                      onClick={focusErrorElement}
                    >
                      Salvar
                    </Styled.Button>
                  </Styled.Footer>
                )}
              </>
            )}
            <FuelToast
              ref={successToastReference}
              message="Atualizações salvas."
              heading="Sucesso!"
              severity="success"
            />
            <FuelToast
              ref={errorToastReference}
              message="Tente salvar novamente."
              heading="Algo deu errado!"
              severity="error"
            >
              <FuelButton slot="button" variant="secondary" size="small" type="submit" onClick={focusErrorElement}>
                Salvar
              </FuelButton>
            </FuelToast>
            <Modal
              innerRef={modalRef}
              open={store.saveVehicleDataModal && dirty}
              onConfirm={handleConfirmModal}
              onClose={handleCloseModal}
              onCancel={handleCancelModa}
              title="Deseja salvar?"
              content="Sem salvar a edição de veículo, você perderá todas as informações adicionadas até o momento."
              buttonLabel="Salvar"
              buttonCancelLabel="Não salvar"
            />
          </Styled.Container>
        )
      }}
    </Form>
  )
}

export default VehicleData
